import { components } from "@/lib/ApiSchema";
import CheckButton from "@/lib/CheckButton";
import List, { Column } from "@/lib/List";
import Search from "@/lib/Search";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import dayjs from "dayjs";
import { ReactNode, useRef, useState } from "react";
import { datetimeFormat } from "./timeline/utils";
import CadFileIcon from "~icons/local/cad-file";
import PdfFileIcon from "~icons/local/pdf-file";
import FileIcon from "~icons/local/file";
import UploadIcon from "~icons/local/upload-noborder";
import $api from "@/lib/queries/openapi";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import useModal from "@/lib/modal/useModal";
import ExitFullScreenIcon from "~icons/local/exit-full-screen";
import Online3DViewer from "@/lib/Online3DViewer";
import Card from "@/lib/Card";
import useUploadFiles from "@/lib/queries/useUploadFiles";

export default function Files() {
  const { showModal, closeModal } = useModal();
  const { teamSlug } = useParams();
  const [search, setSearch] = useState("");
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [showAssigned, setShowAssigned] = useState(false);
  const [showCadModel, setShowCadModel] = useState(true);
  const [showPdf, setShowPdf] = useState(true);
  const [showOther, setShowOther] = useState(true);
  const quoteRevision = useGetCurrentQuoteRevision();
  const canEditQuoteRevision = quoteRevision?.can_edit;
  const queryClient = useQueryClient();
  const uploadFilesRef = useRef<HTMLInputElement>(null);

  const { mutate: updateQuoteRevision } = $api.useMutation(
    "patch",
    "/api/teams/{team_slug}/quote_revisions/{id}",
  );

  const { uploadFiles } = useUploadFiles({
    onSuccess: (fileIds) => {
      updateQuoteRevision(
        {
          params: {
            path: {
              id: quoteRevision?.id ?? "",
              team_slug: teamSlug!,
            },
          },
          body: {
            file_ids: fileIds,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
            });
            toast.success("Files uploaded successfully");

            if (uploadFilesRef.current) {
              uploadFilesRef.current.value = "";
            }
          },
        },
      );
    },
  });

  if (!quoteRevision) return null;

  const { files, requested_part_revisions } = quoteRevision;
  const assignedFileIds = requested_part_revisions.reduce(
    (ids, part) => [
      ...ids,
      part.cad_model?.id ?? "",
      part.technical_drawing?.id ?? "",
    ],
    [] as string[],
  );
  const showFiles = files.filter(
    ({ id, file_name, content_type }) =>
      ((showAssigned && assignedFileIds.includes(id)) ||
        (showUnassigned && !assignedFileIds.includes(id))) &&
      ((showCadModel && content_type === "model/step") ||
        (showPdf && content_type === "application/pdf") ||
        (showOther &&
          !["application/pdf", "model/step"].includes(content_type))) &&
      file_name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card
      header={
        <div className="flex items-center justify-between">
          <div className="flex gap-16">
            <div className="font-bold text-lg">Received Files</div>
            <div className="flex divide-x">
              <div className="flex gap-4 px-4">
                <CheckButton
                  label="Unassigned"
                  checked={showUnassigned}
                  onChange={() => setShowUnassigned(!showUnassigned)}
                />
                <CheckButton
                  label="Assigned"
                  checked={showAssigned}
                  onChange={() => setShowAssigned(!showAssigned)}
                />
              </div>
              <div className="flex gap-4 px-4">
                <CheckButton
                  label="CAD Model"
                  checked={showCadModel}
                  onChange={() => setShowCadModel(!showCadModel)}
                />
                <CheckButton
                  label="PDF"
                  checked={showPdf}
                  onChange={() => setShowPdf(!showPdf)}
                />
                <CheckButton
                  label="Other"
                  checked={showOther}
                  onChange={() => setShowOther(!showOther)}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-8 items-center">
            {canEditQuoteRevision && (
              <label className="flex gap-2 items-center select-none cursor-pointer rounded-sm py-2 px-5 text-sm border bg-white hover:bg-gray-100">
                <input
                  type="file"
                  className="hidden"
                  multiple
                  ref={uploadFilesRef}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (!files) return;
                    uploadFiles(Array.from(files));
                  }}
                />
                <UploadIcon />
                Upload Files
              </label>
            )}
            <Search onChange={(value) => setSearch(value)} value="" />
          </div>
        </div>
      }
    >
      <List<components["schemas"]["QuoteRevisionFileSchema"]>
        columns={columns}
        list={showFiles}
        header={false}
        noBorder
        emptyState={
          <div className="text-center text-gray-500 py-8">
            No files found. Check filters.
          </div>
        }
        clickRow={(file) => {
          if (
            ["application/pdf", "model/step"].includes(file.content_type) ||
            isImageFile(file.content_type)
          ) {
            showModal(
              <FileModalLayout closeModal={closeModal}>
                <FileContent file={file} />
              </FileModalLayout>,
            );
          }
        }}
      />
    </Card>
  );
}

const columns: Column<components["schemas"]["QuoteRevisionFileSchema"]>[] = [
  {
    content: ({ file_name, content_type }) => (
      <div className="flex gap-4">
        {fileTypeToIcon(content_type)}
        <div className="text-sm">{file_name}</div>
      </div>
    ),
  },
  {
    content: ({ created_at }) => dayjs(created_at).format(datetimeFormat),
  },
  {
    content: () => null,
  },
];

function fileTypeToIcon(fileType: string) {
  switch (fileType) {
    case "model/step":
      return <CadFileIcon className="size-5" />;
    case "application/pdf":
      return <PdfFileIcon className="size-5" />;
    default:
      return <FileIcon className="size-5" />;
  }
}

function FileModalLayout({
  closeModal,
  children,
}: {
  closeModal: () => void;
  children: ReactNode;
}) {
  return (
    <div className="w-screen h-screen flex flex-col items-center">
      <ExitFullScreenIcon
        className="cursor-pointer fixed left-7 top-7"
        onClick={closeModal}
      />
      {children}
    </div>
  );
}

function FileContent({
  file,
}: {
  file: components["schemas"]["QuoteRevisionFileSchema"];
}) {
  const { content_type, url, embed_url } = file;
  if (content_type === "application/pdf") {
    return (
      <iframe
        src={`${url}#navpanes=0&view=FitH&zoom=page-fit`}
        className="h-full w-full"
      />
    );
  }

  if (content_type === "model/step") {
    return <Online3DViewer className="grow w-full" fileUrl={embed_url} />;
  }

  if (isImageFile(content_type)) {
    return <img src={url} />;
  }
}

function isImageFile(content_type: string) {
  return content_type.split("/")[0] === "image";
}
